/**
 * Story #6490 (Bug #4838) 8.1 — the inventor page's revoke call.
 *
 * Revoking a key now DELETES the search entries and derived vectors it covers,
 * and on the tiers sold with "security-key step-up on every key operation" the
 * API answers 403 {requires_webauthn: true} until it gets a fresh single-use
 * step-up token. So: try once, and on that 403 run the ceremony for the
 * 'revoke-key' operation and retry exactly once. stepUp / webAuthnAvailable are
 * parameters (defaulting to the real browser ceremony) so the flow is testable
 * without replacing any module.
 */
import { stepUp as realStepUp, isWebAuthnAvailable, NO_AUTHENTICATOR } from '../../services/webauthnClient'
import { notifyIfKeyRevoked, KEY_REVOKED_TEXT } from '../../services/api'

export interface VectorPurge {
  per_index?: Record<string, { matched?: number; deleted?: number }>
  status?: string
  purge_state?: string
  purge_started_at?: string
  retained_in_backups_until?: string
  retained_in_backups_detail?: string
  error?: string
}

export interface RevokeResponse {
  message?: string
  status?: string
  key_disabled?: boolean
  key_disable_state?: string
  vector_purge?: VectorPurge
}

export type RevokeOutcome = { ok: true; data: RevokeResponse } | { ok: false; error: string }

export interface RevokeDeps {
  stepUp?: (operation: string) => Promise<string>
  webAuthnAvailable?: () => boolean
}

export const NEEDS_AUTHENTICATOR_TEXT =
  'Revoking a key on your custody tier needs a security key. Register one under Manage devices, then try again.'

async function post(url: string, token: string, body: object) {
  const resp = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    body: JSON.stringify(body),
  })
  let data: Record<string, unknown> = {}
  try { data = await resp.json() } catch { /* non-JSON body: reported by status below */ }
  return { status: resp.status, ok: resp.ok, data }
}

function failureText(status: number, data: Record<string, unknown>): string {
  if (notifyIfKeyRevoked(status, data)) return KEY_REVOKED_TEXT
  if (typeof data.error === 'string' && data.error) return data.error
  if (status === 401) return 'Please sign in to manage encryption keys.'
  if (status >= 500) return 'Something went wrong on our end. Please try again or contact support.'
  return `Revoke failed (${status}).`
}

export async function revokeCustomerKey(
  url: string, authToken: string, body: Record<string, unknown>, deps: RevokeDeps = {},
): Promise<RevokeOutcome> {
  const stepUp = deps.stepUp ?? realStepUp
  const webAuthnAvailable = deps.webAuthnAvailable ?? isWebAuthnAvailable

  let r = await post(url, authToken, body)
  if (r.status === 403 && r.data.requires_webauthn === true) {
    if (!webAuthnAvailable()) {
      return { ok: false, error: 'Revoking this key needs a security key, but this browser doesn’t support WebAuthn.' }
    }
    let stepUpToken: string
    try {
      stepUpToken = await stepUp('revoke-key')
    } catch (err) {
      if ((err as Error)?.message === NO_AUTHENTICATOR) return { ok: false, error: NEEDS_AUTHENTICATOR_TEXT }
      return { ok: false, error: (err as Error)?.message || 'Security-key check failed.' }
    }
    // Exactly one retry: the token is single-use, so a second 403 is the answer.
    r = await post(url, authToken, { ...body, webauthn_token: stepUpToken })
  }
  if (!r.ok) return { ok: false, error: failureText(r.status, r.data) }
  return { ok: true, data: r.data as RevokeResponse }
}

/** Total search entries deleted across every index in a vector_purge report. */
export function vectorsDeleted(purge?: VectorPurge | null): number | null {
  if (!purge?.per_index) return null
  return Object.values(purge.per_index).reduce((n, v) => n + (v?.deleted ?? 0), 0)
}

/**
 * The deletion state to show after a revoke. The key list is re-read after the
 * revoke (and polled while the deletion runs), so its persisted purge_state is
 * the freshest answer and wins; the response's vector_purge is the answer at
 * the instant of the click, used until the list has the row. With neither, the
 * honest answer is "in progress" — never an error toast (8.2).
 */
export function purgeStateAfterRevoke(resp: RevokeResponse | null | undefined, listedPurgeState?: string | null): string {
  return listedPurgeState || resp?.vector_purge?.purge_state || resp?.vector_purge?.status || 'in_progress'
}

/** States the deletion can still move out of — the page keeps re-reading while in one. */
export const RUNNING_PURGE_STATES = new Set(['in_progress', 'owed'])

/** The registry key_type the API resolves a revoke to (unique per tenant, unlike an ARN). */
export function registryKeyType(scopeType?: string, scopeId?: string): string {
  return !scopeType || scopeType === 'org' || !scopeId ? 'primary' : `scoped#${scopeType}#${scopeId}`
}
